// Email Connection Hook
// React hook for managing Gmail email connections

import { useState, useEffect, useCallback } from 'react';
import emailService from '../../services/emailService';
import { useAuth } from '../../contexts/AuthContext';
import { trackEmailIntegration } from '../../utils/analytics';

export function useEmailConnection() {
  const { user } = useAuth();
  const [connectionState, setConnectionState] = useState({
    isLoading: false,
    isConnected: false,
    connection: null,
    syncStatus: null,
    error: null
  });

  // Load connection data when user changes
  useEffect(() => {
    console.log('🔍 useEmailConnection: User changed:', user?.id, user?.email);
    
    if (!user) {
      console.log('🔍 useEmailConnection: User logged out, clearing connection state');
      // Clear connection state when user logs out
      setConnectionState({
        isLoading: false,
        isConnected: false,
        connection: null,
        syncStatus: null,
        error: null
      });
    } else {
      console.log('🔍 useEmailConnection: User logged in, loading connection for:', user.id);
      // Load connection data for the current user
      loadUserConnection();
    }
  }, [user]);

  /**
   * Load email connection data for current user
   */
  const loadUserConnection = async () => {
    try {
      console.log('🔍 useEmailConnection: Loading connection for user:', user?.id);
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      if (!user) {
        console.log('🔍 useEmailConnection: No user, clearing state');
        setConnectionState(prev => ({
          ...prev,
          isLoading: false,
          isConnected: false,
          connection: null,
          syncStatus: null
        }));
        return;
      }

      const connection = await emailService.getEmailConnection(user.id);
      const syncStatus = await emailService.getEmailSyncStatus(user.id);

      console.log('🔍 useEmailConnection: Loaded connection:', !!connection, 'syncStatus:', syncStatus);

      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        isConnected: !!connection,
        connection,
        syncStatus
      }));
    } catch (error) {
      console.error('🔍 useEmailConnection: Error loading email connection:', error);
      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
    }
  };

  /**
   * Save email connection from OAuth data
   * @param {Object} oauthData - OAuth data from Gmail component
   */
  const saveConnection = useCallback(async (oauthData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      const connection = await emailService.saveEmailConnection(user.id, oauthData);
      const syncStatus = await emailService.getEmailSyncStatus(user.id);

      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        isConnected: true,
        connection,
        syncStatus
      }));
      
      // Track email connection
      trackEmailIntegration('connect', { provider: 'gmail' });

      return connection;
    } catch (error) {
      console.error('Error saving email connection:', error);
      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
      throw error;
    }
  }, [user]);

  /**
   * Toggle email sync
   * @param {boolean} enabled - Whether to enable sync
   */
  const toggleSync = useCallback(async (enabled) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      await emailService.toggleEmailSync(user.id, enabled);
      
      // Reload connection data
      await loadUserConnection();

    } catch (error) {
      console.error('Error toggling email sync:', error);
      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
      throw error;
    }
  }, [user]);

  /**
   * Delete email connection
   */
  const deleteConnection = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      await emailService.deleteEmailConnection(user.id);

      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        isConnected: false,
        connection: null,
        syncStatus: null
      }));
      
      // Track email disconnection
      trackEmailIntegration('disconnect', { provider: 'gmail' });

    } catch (error) {
      console.error('Error deleting email connection:', error);
      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
      throw error;
    }
  }, [user]);

  /**
   * Refresh Gmail tokens
   */
  const refreshTokens = useCallback(async () => {
    if (!user || !connectionState.connection) {
      throw new Error('No email connection found');
    }

    try {
      setConnectionState(prev => ({ ...prev, isLoading: true, error: null }));

      const decryptedConnection = await emailService.getDecryptedEmailConnection(user.id);
      
      if (!decryptedConnection?.refresh_token) {
        throw new Error('No refresh token available');
      }

      const newTokens = await emailService.refreshGmailToken(decryptedConnection.refresh_token);
      
      // Update connection with new tokens
      await emailService.updateEmailConnection(user.id, {
        access_token: newTokens.access_token,
        refresh_token: newTokens.refresh_token || decryptedConnection.refresh_token
      });

      // Reload connection data
      await loadUserConnection();

    } catch (error) {
      console.error('Error refreshing tokens:', error);
      setConnectionState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message
      }));
      throw error;
    }
  }, [user, connectionState.connection]);

  /**
   * Get decrypted connection (for API calls)
   */
  const getDecryptedConnection = useCallback(async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      return await emailService.getDecryptedEmailConnection(user.id);
    } catch (error) {
      console.error('Error getting decrypted connection:', error);
      throw error;
    }
  }, [user]);

  /**
   * Validate connection
   */
  const validateConnection = useCallback(async () => {
    if (!user) {
      return false;
    }

    try {
      return await emailService.validateEmailConnection(user.id);
    } catch (error) {
      console.error('Error validating connection:', error);
      return false;
    }
  }, [user]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setConnectionState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Refresh connection data
   */
  const refresh = useCallback(() => {
    loadUserConnection();
  }, [user]);

  return {
    // State
    isLoading: connectionState.isLoading,
    isConnected: connectionState.isConnected,
    connection: connectionState.connection,
    syncStatus: connectionState.syncStatus,
    error: connectionState.error,
    user,

    // Actions
    saveConnection,
    toggleSync,
    deleteConnection,
    refreshTokens,
    getDecryptedConnection,
    validateConnection,
    clearError,
    refresh
  };
}

export default useEmailConnection;
